import type { AxiosError } from 'axios'
import axios from 'axios'
import Cookies from 'js-cookie'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import * as Sentry from '@sentry/react'
import qs from 'qs'

import type { RefreshTokenRequest, RefreshTokenResponse } from '@models/Account'
import { clearTokens, readStoredToken, setTokens } from './token'
import { API_ADDRESS } from '../constants'

import router from '@src/Routes'
import routes from '@src/Routes/routes'
import queryClient from '@helpers/queryClient'
import useStore from '@src/store'

export const REFRESH_TOKEN_PATH = '/api/token/refresh/'

const axiosInstance = axios.create({
  baseURL: API_ADDRESS,
  withCredentials: true,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
})

const refreshToken = async (): Promise<string | void> => {
  const refreshToken = localStorage.getItem('refreshToken') || Cookies.get('refreshToken')

  if (!refreshToken || refreshToken === 'undefined') {
    throw new Error('No refresh token available')
  }
  try {
    const refreshResponse = await axios.post<RefreshTokenResponse>(
      REFRESH_TOKEN_PATH,
      {
        refresh: refreshToken,
      } as RefreshTokenRequest,
      {
        baseURL: API_ADDRESS,
        withCredentials: true,
      },
    )

    setTokens(refreshResponse.data.access, refreshResponse.data.refresh)
    return refreshResponse.data.access
  } catch (err) {
    if (err) {
      clearTokens()
      delete axiosInstance.defaults.headers.Authorization
      localStorage.clear()
      useStore.getState().reset()
      queryClient.removeQueries()
      await router.navigate(routes.public.login, {
        state: {
          prev: window.location.pathname,
        },
      })
      Sentry.captureException(err)
      throw err
    }
  }
}

// Single-flight guard around refreshToken. axios-auth-refresh already
// serializes refreshes for axios calls; this extends the same guarantee to
// non-axios callers (authorizedFetch's SSE streams). The backend rotates
// refresh tokens on every use, so two concurrent refreshes race
// last-writer-wins on the rotated pair — and would hard-logout users if
// blacklist-after-rotation were ever enabled server-side.
let refreshInFlight: Promise<string | void> | null = null

export const refreshTokenOnce = (): Promise<string | void> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshToken().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

createAuthRefreshInterceptor(axiosInstance, refreshTokenOnce, {
  // Refresh the access token and retry on ANY 401, including integration
  // endpoints (/quickbooks, /xero, /softledger). These used to be excluded
  // because the previous interceptor logged the user out on a 401, which we
  // didn't want for integration calls. axios-auth-refresh instead refreshes once
  // and retries once (it never loops), so excluding integrations only stranded
  // their lazily-loaded dropdowns on an expired-session 401 until a full page
  // reload — e.g. opening the SL Project field after the token expired returned
  // 401 and rendered "No options" with no recovery.
  shouldRefresh: (error: AxiosError) => error.response?.status === 401,
  onRetry: (requestConfig) => {
    if (requestConfig.headers) {
      requestConfig.headers.Authorization = axiosInstance.defaults.headers.Authorization
    }
    return requestConfig
  },
})

axiosInstance.interceptors.request.use((config) => {
  const token = readStoredToken()

  if (token) {
    config.headers.Authorization = `Token ${token}`
  }

  return config
})

export default axiosInstance
